import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Clock,
  Mail,
  ArrowRight,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import axios from "axios";

const CalendarModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState("calendar");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    company: "",
    employees: "50-100",
    botcheck: "", // honeypot
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setStep("calendar");
      setErrorMessage("");
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // prevent double click

    setIsSubmitting(true);
    setStep("loading");

    try {
      const response = await axios.post(
        "https://api.web3forms.com/submit",
        {
          access_key: "7932a007-ce1b-4742-a255-39352f971f61",

          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          subject: "New Demo Booking - Zenova",
          message: `
New Demo Booking Request

Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company}
Employees: ${formData.employees}
Source: calendar_modal
          `,
          botcheck: formData.botcheck,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.success) {
        setStep("confirm");
        setFormData({
          name: "",
          email: "",
          mobile: "",
          company: "",
          employees: "50-100",
          botcheck: "",
        });
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Failed to submit booking. Please try again.");
      setStep("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
        >
          <X size={20} />
        </button>

        {/* ================= CALENDAR FORM ================= */}
        {step === "calendar" && (
          <>
            <div className="p-6 text-center bg-green-900 text-white">
              <Calendar size={32} className="mx-auto mb-3" />
              <h2 className="text-2xl font-bold">Book Your Free Demo</h2>
              <div className="flex items-center justify-center gap-2 text-sm opacity-80">
                <Clock size={16} />7 Minutes • No Commitment
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Honeypot hidden field */}
              <input
                type="text"
                name="botcheck"
                value={formData.botcheck}
                onChange={(e) =>
                  setFormData({ ...formData, botcheck: e.target.value })
                }
                className="hidden"
              />

              <input
                type="text"
                required
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 py-3 border rounded-xl"
              />

              <input
                type="email"
                required
                placeholder="Work Email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-4 py-3 border rounded-xl"
              />

              <input
                type="tel"
                required
                pattern="[0-9]{10}"
                placeholder="Mobile Number"
                value={formData.mobile}
                onChange={(e) =>
                  setFormData({ ...formData, mobile: e.target.value })
                }
                className="w-full px-4 py-3 border rounded-xl"
              />

              <input
                type="text"
                required
                placeholder="Company Name"
                value={formData.company}
                onChange={(e) =>
                  setFormData({ ...formData, company: e.target.value })
                }
                className="w-full px-4 py-3 border rounded-xl"
              />

              <select
                value={formData.employees}
                onChange={(e) =>
                  setFormData({ ...formData, employees: e.target.value })
                }
                className="w-full px-4 py-3 border rounded-xl"
              >
                <option value="1-50">1-50 employees</option>
                <option value="50-100">50-100 employees</option>
                <option value="100-200">100-200 employees</option>
                <option value="200-300">200-300 employees</option>
                <option value="300-500">300-500 employees</option>
                <option value="500+">500+ employees</option>
              </select>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-green-700 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                {isSubmitting ? "Submitting..." : "Book Demo Now"}
                <ArrowRight size={18} />
              </button>

              <p className="text-xs text-center text-gray-500">
                Our team will contact you within 2 hours
              </p>
            </form>
          </>
        )}

        {/* ================= LOADING ================= */}
        {step === "loading" && (
          <div className="p-10 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-green-700 border-t-transparent rounded-full mx-auto mb-4" />
            <p>Submitting your request...</p>
          </div>
        )}

        {/* ================= SUCCESS ================= */}
        {step === "confirm" && (
          <div className="p-10 text-center">
            <CheckCircle size={48} className="mx-auto text-green-600 mb-4" />
            <h2 className="text-xl font-bold mb-2">Booking Received!</h2>
            <p className="text-gray-600 mb-4">
              Our team will contact you within 2 hours.
            </p>
            <button
              onClick={onClose}
              className="bg-green-700 text-white px-6 py-3 rounded-xl"
            >
              Done
            </button>
          </div>
        )}

        {/* ================= ERROR ================= */}
        {step === "error" && (
          <div className="p-10 text-center">
            <AlertCircle size={48} className="mx-auto text-red-600 mb-4" />
            <h2 className="text-xl font-bold mb-2 text-red-700">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <button
              onClick={() => setStep("calendar")}
              className="bg-green-700 text-white px-6 py-3 rounded-xl"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarModal;
